import React, { useState } from "react";
import { Drama, ChevronDown } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtPts } from "@/components/thespian/ThespianRankCard";

const RANK_TONE = {
  "Thespian": "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
  "Honor Thespian": "bg-amber-200 text-amber-900",
  "National Honor Thespian": "bg-sky-100 text-sky-800",
  "International Honor Thespian": "bg-violet-100 text-violet-800",
};

export function RankChip({ rank }) {
  if (!rank) return <span className="text-xs text-slate-400">Not yet inducted</span>;
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${RANK_TONE[rank]}`}>{rank}</span>;
}

// Director: every team member's cumulative ITS points and rank.
export default function ThespianRoster({ roster }) {
  const [open, setOpen] = useState(false);
  if (!roster.length) return null;
  return (
    <Card className="mb-4 overflow-hidden border-slate-200 shadow-sm" data-testid="thespian-roster-card">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 border-b border-slate-100 px-4 py-3 text-left transition-colors hover:bg-slate-50" data-testid="thespian-roster-toggle" aria-expanded={open}>
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-700 ring-1 ring-amber-200" data-testid="thespian-roster-icon"><Drama className="h-4 w-4" /></span>
        <div>
          <h3 className="font-heading text-sm font-bold text-slate-800">Team ITS Ranks</h3>
          <p className="text-xs text-slate-500">Cumulative approved points per member.</p>
        </div>
        <ChevronDown className={`ml-auto h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="max-h-96 overflow-auto">
          <Table data-testid="thespian-roster-table">
            <TableHeader><TableRow className="bg-slate-50">{["Member", "Points", "Rank", "Next rank", "Pending"].map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {roster.map((m) => (
                <TableRow key={m.member_id} data-testid={`thespian-roster-row-${m.member_id}`}>
                  <TableCell className="text-sm font-medium text-slate-800">{m.name}</TableCell>
                  <TableCell className="font-bold tabular-nums text-amber-700" data-testid={`thespian-roster-points-${m.member_id}`}>{fmtPts(m.total_points)}</TableCell>
                  <TableCell data-testid={`thespian-roster-rank-${m.member_id}`}><RankChip rank={m.rank} /></TableCell>
                  <TableCell className="text-xs text-slate-500">{m.next_rank ? `${fmtPts(m.points_to_next)} to ${m.next_rank}` : "Top rank"}</TableCell>
                  <TableCell>{m.pending ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">{m.pending}</span> : <span className="text-xs text-slate-300">—</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}
